import { getSceneState } from "../stores/sceneStore";
import { usePhysicsStore } from "../stores/physicsStore";
import {
  buildCurrentSceneInitialJointMap,
  getInitialFreeJointPoseFromScene,
} from "../stores/physics/physicsStartupHelpers";
import type { BodyUUID, Vector3 } from "../types/scene";
import { logger } from "../utils/logger";
import { prepareCurrentPhysicsWorkerInput } from "./mujocoBridge";
import { getThreadedWasmSupport } from "./threadedWasmSupport";

type WorkerStaticInterferenceContact = {
  index: number;
  geom1: number;
  geom2: number;
  geom1Name: string;
  geom2Name: string;
  body1Id: number;
  body2Id: number;
  body1Name: string;
  body2Name: string;
  distance: number;
  position: Vector3;
};

type WorkerStaticInterferencePayload = {
  contactCount: number;
  penetrationCount: number;
  contacts: WorkerStaticInterferenceContact[];
};

export type StaticInterferenceContact = WorkerStaticInterferenceContact & {
  body1Uuid: BodyUUID | null;
  body2Uuid: BodyUUID | null;
  body1DisplayName: string;
  body2DisplayName: string;
};

export type StaticInterferenceResult = {
  contactCount: number;
  penetrationCount: number;
  contacts: StaticInterferenceContact[];
  loadMode: string;
  missingMeshes: string[];
  computedAt: number;
};

const STATIC_INTERFERENCE_TIMEOUT_MS = 30000;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const coerceBodyUuidFromMujocoName = (name: string): BodyUUID | null => {
  const raw = String(name || "").trim();
  if (!raw || raw === "world") return null;
  const maybe = raw.startsWith("b_") ? raw.slice(2) : raw;
  return UUID_RE.test(maybe) ? (maybe as BodyUUID) : null;
};

const mapStaticInterferenceContacts = (
  payload: WorkerStaticInterferencePayload
): StaticInterferenceContact[] => {
  const scene = getSceneState();
  const bodies = scene.bodies || {};
  return (payload.contacts || []).map((contact) => {
    const body1Uuid = coerceBodyUuidFromMujocoName(contact.body1Name);
    const body2Uuid = coerceBodyUuidFromMujocoName(contact.body2Name);
    return {
      ...contact,
      body1Uuid: body1Uuid && bodies[body1Uuid] ? body1Uuid : null,
      body2Uuid: body2Uuid && bodies[body2Uuid] ? body2Uuid : null,
      body1DisplayName:
        body1Uuid && bodies[body1Uuid]?.name ? bodies[body1Uuid].name : contact.body1Name,
      body2DisplayName:
        body2Uuid && bodies[body2Uuid]?.name ? bodies[body2Uuid].name : contact.body2Name,
    };
  });
};

export async function computeStaticInterferenceFromCurrentScene(): Promise<StaticInterferenceResult> {
  const threadedWasmSupport = getThreadedWasmSupport();
  if (!threadedWasmSupport.ok) {
    throw new Error(threadedWasmSupport.message);
  }
  if (usePhysicsStore.getState().isRunning || usePhysicsStore.getState().isPaused) {
    throw new Error("Static interference checks are disabled while physics is running.");
  }

  const startupPosePolicy = "currentScenePose";
  const sceneState = getSceneState();
  const { xmlString, assets, bodyNamesHint, loadMode, missingMeshes } =
    await prepareCurrentPhysicsWorkerInput({
      startupPosePolicy,
    });

  return await new Promise<StaticInterferenceResult>((resolve, reject) => {
    const worker = new Worker(new URL("./mujocoWorker.worker.js", import.meta.url), {
      type: "module",
      credentials: "same-origin",
    });
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      try {
        worker.terminate();
      } catch (error) {
        logger.warn("[measurements] Failed to terminate timed-out worker", error);
      }
      reject(new Error("Timed out while checking static interference."));
    }, STATIC_INTERFERENCE_TIMEOUT_MS);

    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      try {
        worker.terminate();
      } catch (error) {
        logger.warn("[measurements] Failed to terminate worker", error);
      }
      callback();
    };

    worker.onerror = (event) => {
      finish(() => reject(new Error(event.message || "Static interference worker failed.")));
    };
    worker.onmessageerror = () => {
      finish(() =>
        reject(new Error("Static interference worker message could not be deserialized."))
      );
    };
    worker.onmessage = (event) => {
      const message = event.data || {};
      if (!message?.type) return;
      if (message.type === "worker-loaded") {
        worker.postMessage({ type: "hello" });
        return;
      }
      if (message.type === "ready") {
        worker.postMessage({
          type: "init",
          payload: {
            xmlString,
            assets,
            timestep: usePhysicsStore.getState().timestep,
            realtimeScale: usePhysicsStore.getState().realtimeScale,
            solverIterations: usePhysicsStore.getState().solverIterations,
            solverTolerance: usePhysicsStore.getState().solverTolerance,
            loadMode,
            bodyNamesHint,
            analysisOnly: true,
            analysisKind: "staticInterference",
            initialJointMap: buildCurrentSceneInitialJointMap(xmlString, sceneState),
            initialFreeJointPose: getInitialFreeJointPoseFromScene(sceneState),
            initialControls: null,
            startupPosePolicy,
            preserveActuatorControls: false,
          },
        });
        return;
      }
      if (message.type === "staticInterference") {
        const payload = message.payload as WorkerStaticInterferencePayload;
        finish(() =>
          resolve({
            contactCount: Number(payload?.contactCount) || 0,
            penetrationCount: Number(payload?.penetrationCount) || 0,
            contacts: mapStaticInterferenceContacts(payload),
            loadMode,
            missingMeshes: missingMeshes || [],
            computedAt: Date.now(),
          })
        );
        return;
      }
      if (message.type === "error") {
        const errorMessage = String(message.payload?.error || message.error || "Worker error");
        finish(() => reject(new Error(errorMessage)));
      }
    };
  });
}
